import { useState, useEffect, useMemo } from "react"
import { Search } from "lucide-react"
import type { PageCursor, Service, UserProfile } from "@ezbatna/core/domain/models"
import { getContainer } from "../lib/container"
import { Button, ConfirmDialog, DataTable, Input, useToast, type DataTableColumn } from "../components/ui"

const PAGE_SIZE = 20
const APP_URL = "https://app.ezbatna.com"

export function ServicesPage() {
  const [services, setServices] = useState<Service[]>([])
  const [owners, setOwners] = useState<Record<string, UserProfile>>({})
  const [cursor, setCursor] = useState<PageCursor | null>(null)
  const [loading, setLoading] = useState(true)
  const [confirmTarget, setConfirmTarget] = useState<Service | null>(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [search, setSearch] = useState("")
  const [userFilter, setUserFilter] = useState("")
  const toast = useToast()

  const resolveOwners = async (list: Service[]) => {
    const missing = Array.from(new Set(list.map((s) => s.userId))).filter((id) => !owners[id])
    if (missing.length === 0) return
    const profiles = await Promise.all(missing.map((id) => getContainer().userProfileRepository.fetchProfile(id)))
    setOwners((prev) => {
      const next = { ...prev }
      profiles.forEach((p, i) => {
        if (p) next[missing[i]!] = p
      })
      return next
    })
  }

  const fetchServices = async (after?: PageCursor | null) => {
    setLoading(true)
    try {
      const page = await getContainer().serviceRepository.fetchPage(PAGE_SIZE, after)
      setServices((prev) => (after ? [...prev, ...page.items] : page.items))
      setCursor(page.nextCursor)
      resolveOwners(page.items)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchServices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleTakedown = async () => {
    if (!confirmTarget) return
    setActionLoading(true)
    try {
      await getContainer().adminRepository.takedownTarget("service", confirmTarget.id)
      setServices((prev) => prev.filter((s) => s.id !== confirmTarget.id))
      toast.show("تم حذف الخدمة", "success")
    } catch (e) {
      toast.show(`خطأ: ${(e as Error).message}`, "danger")
    } finally {
      setActionLoading(false)
      setConfirmTarget(null)
    }
  }

  const filtered = useMemo(() => {
    let list = services
    const q = search.trim().toLowerCase()
    if (q) {
      list = list.filter((s) =>
        [s.title, s.description, s.location, s.price, s.contactInfo, s.id].some((field) =>
          field?.toLowerCase().includes(q),
        ),
      )
    }
    const uq = userFilter.trim().toLowerCase().replace(/^@/, "")
    if (uq) {
      list = list.filter((s) => {
        const owner = owners[s.userId]
        return owner?.username?.toLowerCase().includes(uq) || s.userId.toLowerCase().includes(uq)
      })
    }
    return list
  }, [services, search, userFilter, owners])

  const openOnApp = (s: Service) => window.open(`${APP_URL}/services/${s.id}`, "_blank", "noopener,noreferrer")

  const columns: DataTableColumn<Service>[] = [
    { key: "title", header: "العنوان", render: (s) => <span className="line-clamp-2 max-w-[280px] font-semibold">{s.title}</span> },
    {
      key: "userId",
      header: "المستخدم",
      render: (s) => {
        const owner = owners[s.userId]
        return owner?.username ? (
          <span className="font-mono text-caption text-accent">@{owner.username}</span>
        ) : (
          <span className="font-mono text-caption text-text-secondary">{s.userId?.slice(0, 10)}…</span>
        )
      },
    },
    {
      key: "location",
      header: "الموقع",
      render: (s) => <span className="text-text-secondary">{s.location ?? "—"}</span>,
    },
    { key: "price", header: "السعر", align: "end", render: (s) => s.price ?? "—" },
    {
      key: "actions",
      header: "إجراءات",
      align: "center",
      render: (s) => (
        <Button
          variant="destructive"
          size="sm"
          onClick={(e) => { e.stopPropagation(); setConfirmTarget(s) }}
        >
          حذف
        </Button>
      ),
    },
  ]

  return (
    <div className="flex flex-col gap-lg">
      <div>
        <h1 className="text-display font-bold text-text-primary">الخدمات</h1>
        <p className="mt-2xs text-body-sm text-text-secondary">
          إدارة ومراجعة الخدمات المعلنة — اضغط على أي صف لفتح الخدمة في الموقع
        </p>
      </div>

      <div className="grid gap-md sm:grid-cols-2">
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث في العنوان، الوصف، الموقع، السعر، رقم التواصل…"
        />
        <Input
          value={userFilter}
          onChange={(e) => setUserFilter(e.target.value)}
          placeholder="تصفية حسب اسم المستخدم (@username) أو المعرّف"
        />
      </div>

      {(search || userFilter) && (
        <p className="text-caption text-text-secondary">
          <Search className="me-1 inline size-3.5" />
          {filtered.length} من {services.length} خدمة محمّلة
        </p>
      )}

      <DataTable
        columns={columns}
        rows={filtered}
        rowKey={(s) => s.id}
        loading={loading}
        onRowClick={openOnApp}
        emptyTitle="لا توجد خدمات"
        emptyDescription="لم يتم نشر أي خدمات حتى الآن، أو لا توجد نتائج مطابقة للبحث."
      />

      {cursor && (
        <Button variant="secondary" onClick={() => fetchServices(cursor)} loading={loading} className="self-start">
          عرض المزيد
        </Button>
      )}

      <ConfirmDialog
        open={!!confirmTarget}
        title="حذف هذه الخدمة؟"
        description={
          <>
            سيتم حذف خدمة «{confirmTarget?.title}» نهائياً من التطبيق. هذا الإجراء لا يمكن التراجع عنه.
          </>
        }
        confirmLabel="حذف نهائي"
        destructive
        loading={actionLoading}
        onConfirm={handleTakedown}
        onCancel={() => setConfirmTarget(null)}
      />
    </div>
  )
}
